import React, { useState, useEffect, useCallback } from 'react';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import * as yup from 'yup';
import EditTableModal from './EditTableModal';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { tableHandler } from '../../../../redux/handlers';

const ZONE_STATUS = {
  ACTIVE: 1,
  INACTIVE: 0
};

const INITIAL_ZONE_DATA = {
  zoneID: null,
  zoneName: '',
  zoneAbbreviation: '',
  zoneTableQty: '',
  zoneStatus: ZONE_STATUS.ACTIVE,
  tableList: []
};

const INITIAL_TABLE_DATA = {
  tableID: null,
  tableName: null,
  tableSeat: null,
  tableStatus: null
};

const validationSchema = yup.object().shape({
  zoneName: yup.string().required('กรุณากรอกชื่อโซน'),
  zoneAbbreviation: yup.string().required('กรุณากรอกตัวย่อโซน')
});

export default function ZoneManagementModal({ onSuccess, onHide, handleDelete, zoneId, show, ...rest }) {
  const dispatch = useDispatch();
  const { businessSlug, branchID } = useParams();

  const [isEditTableModalOpen, setIsEditTableModalOpen] = useState(false);
  const [selectedTableData, setSelectedTableData] = useState(INITIAL_TABLE_DATA);
  const [zoneData, setZoneData] = useState(INITIAL_ZONE_DATA);
  const [originalData, setOriginalData] = useState(INITIAL_ZONE_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const hasZoneChanged = useCallback((values, original) => {
    return (
      (values.zoneName ?? '') !== (original.zoneName ?? '') ||
      (values.zoneAbbreviation ?? '') !== (original.zoneAbbreviation ?? '') ||
      (values.zoneStatus ?? ZONE_STATUS.ACTIVE) !== (original.zoneStatus ?? ZONE_STATUS.ACTIVE)
    );
  }, []);

  const transformApiDataToFormData = useCallback(apiData => {
    return {
      zoneID: apiData.id ?? null,
      zoneName: apiData.name ?? '',
      zoneAbbreviation: apiData.prefix ?? '',
      zoneTableQty: apiData.table_capacity ?? '',
      zoneStatus: apiData.is_status ? ZONE_STATUS.ACTIVE : ZONE_STATUS.INACTIVE,
      tableList: (apiData.branchTable ?? []).map(table => ({
        tableID: table.id,
        tableName: table.name,
        tableSeat: table.seat_capacity,
        tableStatus: table.status
      }))
    };
  }, []);

  const createUpdatePayload = useCallback(
    (values, tableQty) => {
      return {
        branch_id: parseInt(branchID),
        name: (values.zoneName || '').trim(),
        prefix: (values.zoneAbbreviation || '').trim(),
        table_capacity: parseInt(tableQty),
        is_status: values.zoneStatus === ZONE_STATUS.ACTIVE
      };
    },
    [branchID]
  );

  const fetchZoneDetail = useCallback(async () => {
    if (!show || !zoneId) return;

    setIsLoading(true);
    try {
      const response = await dispatch(tableHandler.getBranchTableZoneById(businessSlug, zoneId));

      if (!response) {
        throw new Error('ไม่พบข้อมูลโซน');
      }

      const transformedData = transformApiDataToFormData(response);
      setZoneData(transformedData);
      setOriginalData(transformedData);
    } catch (error) {
      console.error('Error fetching zone detail:', error);
    } finally {
      setIsLoading(false);
    }
  }, [show, zoneId, dispatch, businessSlug, transformApiDataToFormData]);

  useEffect(() => {
    fetchZoneDetail();
  }, [fetchZoneDetail]);

  const validateFormBeforeUpdate = useCallback((values, errors) => {
    if (errors.zoneName || errors.zoneAbbreviation) {
      return {
        isValid: false,
        message: 'กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้องก่อนอัพเดทโต๊ะ'
      };
    }

    if (!values.zoneName?.trim()) {
      return {
        isValid: false,
        message: 'กรุณากรอกชื่อโซนก่อนอัพเดทโต๊ะ'
      };
    }

    if (!values.zoneAbbreviation?.trim()) {
      return {
        isValid: false,
        message: 'กรุณากรอกตัวย่อโซนก่อนอัพเดทโต๊ะ'
      };
    }

    return {
      isValid: true,
      message: ''
    };
  }, []);

  const handleUpdateTable = useCallback(
    async (values, setSubmitting, errors, setFieldError) => {
      try {
        const validation = validateFormBeforeUpdate(values, errors);

        if (!validation.isValid) {
          setFieldError(validation.message);
          setSubmitting(false);
          return;
        }

        if (!hasZoneChanged(values, originalData)) {
          onSuccess?.('ไม่มีการเปลี่ยนแปลงข้อมูล');
          setSubmitting(false);
          return;
        }

        const payload = createUpdatePayload(values, originalData.zoneTableQty);
        payload.is_status = true;

        await dispatch(tableHandler.updateBranchTableZone(businessSlug, values.zoneID, payload));
        await fetchZoneDetail();
        onSuccess?.('อัปเดตโซนสำเร็จ');
      } catch (error) {
        console.error('Error updating zone:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      hasZoneChanged,
      originalData,
      createUpdatePayload,
      dispatch,
      businessSlug,
      fetchZoneDetail,
      onSuccess,
      validateFormBeforeUpdate
    ]
  );

  const handleSave = useCallback(
    async (values, setSubmitting) => {
      try {
        if (!hasZoneChanged(values, originalData)) {
          onHide?.();
          return;
        }

        const payload = createUpdatePayload(values, originalData.zoneTableQty);

        await dispatch(tableHandler.updateBranchTableZone(businessSlug, values.zoneID, payload));
        onSuccess?.('บันทึกข้อมูลสำเร็จ');
        onHide?.();
      } catch (error) {
        console.error('Error saving zone:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [hasZoneChanged, originalData, createUpdatePayload, dispatch, businessSlug, onSuccess, onHide]
  );

  const handleTableUpdateSuccess = useCallback(async () => {
    try {
      await fetchZoneDetail();
      onSuccess?.('อัปเดตโต๊ะสำเร็จ');
      setIsEditTableModalOpen(false);
    } catch (error) {
      console.error('Error after table update:', error);
    }
  }, [fetchZoneDetail, onSuccess]);

  const handleRemoveTable = useCallback(tableID => {
    setZoneData(prev => ({
      ...prev,
      tableList: prev.tableList.filter(table => table.tableID !== tableID)
    }));
    setIsEditTableModalOpen(false);
  }, []);

  const handleEditTable = useCallback(
    tableData => {
      setSelectedTableData({
        ...tableData,
        branch_table_zone_id: zoneData.zoneID
      });
      setIsEditTableModalOpen(true);
    },
    [zoneData.zoneID]
  );

  const handleCloseModal = useCallback(() => {
    setIsEditTableModalOpen(false);
    setSelectedTableData(INITIAL_TABLE_DATA);
  }, []);

  const renderTableList = useCallback(
    tableList => {
      if (!tableList?.length) {
        return <div className="text-center py-4 text-muted">ไม่มีโต๊ะในโซนนี้</div>;
      }

      return tableList.map(table => (
        <div className={`bx-table bx-table-${table.tableStatus}`} key={table.tableID}>
          <div className="tc-4 fw-bold mb-auto ms-1">{table.tableName}</div>
          <div className="d-flex align-items-center">
            <div className="iu me-auto">{table.tableSeat}</div>
            <button
              type="button"
              className="btn-edit-st-2"
              onClick={() => handleEditTable(table)}
              aria-label={`แก้ไขโต๊ะ ${table.tableName}`}
            />
          </div>
        </div>
      ));
    },
    [handleEditTable]
  );

  return (
    <Modal show={show} size="lg" centered onHide={onHide} {...rest}>
      <Modal.Body>
        <div className="d-flex align-items-center px-3 pb-2">
          <div className="fs-24 fw-bold me-auto" style={{ color: '#2DC76D' }}>
            จัดการโซน/โต๊ะ
          </div>
          <button onClick={onHide} className="btn btn-close-modal" aria-label="ปิด" />
        </div>

        <div className="px-3">
          <Formik enableReinitialize initialValues={zoneData} validationSchema={validationSchema} onSubmit={handleSave}>
            {({
              handleSubmit,
              handleChange,
              errors,
              values,
              setFieldValue,
              isSubmitting,
              setSubmitting,
              setFieldError
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="row">
                  <Form.Group className="mb-3 form-st-3 pb-1 col-xl-3">
                    <Form.Label className="mb-1">
                      ชื่อโซน <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="zoneName"
                      placeholder="ชื่อโซน"
                      value={values.zoneName}
                      onChange={handleChange}
                      isInvalid={!!errors.zoneName}
                      disabled={isLoading}
                    />
                    <Form.Control.Feedback type="invalid">{errors.zoneName}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3 form-st-3 pb-1 col-xl-3">
                    <Form.Label className="mb-1">
                      ตัวย่อโซน <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="zoneAbbreviation"
                      placeholder="ตัวย่อโซน"
                      value={values.zoneAbbreviation}
                      onChange={handleChange}
                      isInvalid={!!errors.zoneAbbreviation}
                      disabled={isLoading}
                    />
                    <Form.Control.Feedback type="invalid">{errors.zoneAbbreviation}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3 form-st-3 pb-1 col-xl-3">
                    <Form.Label className="mb-1">จำนวนโต๊ะ</Form.Label>
                    <Form.Control
                      type="number"
                      name="zoneTableQty"
                      placeholder="จำนวนโต๊ะ"
                      value={values.zoneTableQty}
                      onChange={handleChange}
                      disabled={true}
                    />
                  </Form.Group>

                  <div className="col-xl-3">
                    <Button
                      type="button"
                      className="btn-st-3 mt-3"
                      style={{ width: '120px' }}
                      onClick={() => handleUpdateTable(values, setSubmitting, errors, setFieldError)}
                      disabled={isSubmitting || isLoading}
                    >
                      อัพเดทโต๊ะ
                    </Button>
                  </div>
                </div>

                <div className="bx-list-desk-m mb-4">
                  {isLoading ? <div className="text-center py-4">กำลังโหลด...</div> : renderTableList(values.tableList)}
                </div>

                <div className="row align-items-center mb-2">
                  <div className="col-xl-6 d-flex align-items-center">
                    <Form.Label className="me-4 mt-2 tc-10">สถานะ</Form.Label>
                    <Form.Check
                      inline
                      label="เปิดใช้งาน"
                      name="zoneStatus"
                      type="radio"
                      id="zone-active"
                      className="form-check-st-2 tc-7"
                      checked={values.zoneStatus === ZONE_STATUS.ACTIVE}
                      onChange={() => setFieldValue('zoneStatus', ZONE_STATUS.ACTIVE)}
                      disabled={isLoading}
                    />
                    <Form.Check
                      inline
                      label="ปิดใช้งาน"
                      name="zoneStatus"
                      type="radio"
                      id="zone-inactive"
                      className="form-check-st-2 tc-7"
                      checked={values.zoneStatus === ZONE_STATUS.INACTIVE}
                      onChange={() => setFieldValue('zoneStatus', ZONE_STATUS.INACTIVE)}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="col-xl-6">
                    <div className="d-flex align-items-center pb-0 btn-group-control">
                      <div className="me-3 ms-auto" />
                      <Button
                        type="button"
                        className="btn-st-6 me-3"
                        style={{ width: '100px' }}
                        onClick={onHide}
                        disabled={isSubmitting}
                      >
                        ยกเลิก
                      </Button>
                      <Button
                        type="submit"
                        className="btn-st-3"
                        style={{ width: '100px' }}
                        disabled={isSubmitting || isLoading}
                      >
                        บันทึก
                      </Button>
                    </div>
                  </div>
                </div>

                <EditTableModal
                  show={isEditTableModalOpen}
                  onHide={handleCloseModal}
                  handleDelete={handleRemoveTable}
                  handleEdit={handleTableUpdateSuccess}
                  formtabledata={selectedTableData}
                />
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
}
